import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { landingPageEventTypeIds } from '@ps/web-sports-config';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import { TabLinkMenu } from '@swf/sports/components';
import { isMeetingBasedEventTypeId } from '@swf/sports/utils/isEventTypeId';

import type { TabConfig, TabConfigItem, TabKey } from '../../types';

import {
  TXTCLI_Next,
  TXTWCL_Competitions,
  TXTWCL_Featured,
  TXTWCL_Future_1,
  TXTWCL_In_Play,
  TXTWCL_Matches,
  TXTWCL_Meetings,
  TXTWCL_Specials
} from './Tabs.messages.json';

const tabsConfig = (
  isEventTypeLandingPage: boolean,
  isAnimalRacingSpecialsPagesEnabled: boolean,
  eventTypeId?: string
): Partial<Record<TabKey, TabConfigItem>> => {
  const parsedEventTypeId = Number(eventTypeId);

  return {
    IN_PLAY: {
      alias: 'sportsSportInPlay',
      translation: TXTWCL_In_Play
    },
    MATCHES: {
      alias: 'sportsMatchFiltering',
      translation: TXTWCL_Matches
    },
    COMPETITIONS: {
      alias: isEventTypeLandingPage ? 'sportsCompetitions' : 'sportsSport',
      translation: TXTWCL_Competitions
    },
    SPECIALS: {
      alias: 'sportsSpecials',
      translation: TXTWCL_Specials
    },
    ...(isMeetingBasedEventTypeId(parsedEventTypeId) && {
      NEXT_RACES: {
        alias: 'sportsAnimalRacingNext',
        translation: TXTCLI_Next
      },
      RACE_MEETINGS: {
        alias: 'sportsAnimalRacingMeetings',
        translation: TXTWCL_Meetings
      },
      FUTURE: {
        alias: 'sportsAnimalRacingFuture',
        translation: TXTWCL_Future_1
      },
      ...(isAnimalRacingSpecialsPagesEnabled && {
        RACE_SPECIALS: {
          alias: 'sportsAnimalRacingSpecials',
          translation: TXTWCL_Specials
        }
      })
    }),
    ...(isEventTypeLandingPage && {
      LANDING: {
        alias: 'sportsLanding',
        translation: TXTWCL_Featured
      }
    })
  } satisfies Partial<Record<TabKey, TabConfigItem>>;
};

export interface Props {
  tabs: TabConfig[];
}

const Tabs: React.FC<Props> = ({ tabs }) => {
  const pageAlias = useSelector(select.cms.getPage)?.alias;
  const urlParams = useSelector(select.sports.getUrlParams);
  const isEventTypeLandingPage = useSelector((state: RootState) =>
    select.sports.isEventTypeLandingPage(state, { landingPageEventTypeIds })
  );
  const isAnimalRacingSpecialsPagesEnabled = useFeature(FeatureFlag.ISPAnimalRacingSpecials);

  if (!tabs.length) {
    return null;
  }

  const isInPlayPage = pageAlias === 'sportsSportInPlay';

  return (
    <TabLinkMenu>
      {tabs.map((tab) => {
        const { name } = tab;

        // always show in-play tab when inPlayEvents > 0 or if on in-play page
        if (name === 'IN_PLAY' && tab.inPlayEventsCount === 0 && !isInPlayPage) {
          return null;
        }

        const tabConfig = tabsConfig(
          isEventTypeLandingPage,
          isAnimalRacingSpecialsPagesEnabled,
          urlParams.eventTypeId
        )[name];

        if (!tabConfig) return null;

        const { alias, translation } = tabConfig;

        return (
          <TabLinkMenu.Link
            alias={alias}
            values={urlParams}
            key={name}
            data-testid={`tab-menu-link-${name.toLowerCase()}`}
          >
            <Translation {...translation} /> {name === 'IN_PLAY' && `(${tab.inPlayEventsCount})`}
          </TabLinkMenu.Link>
        );
      })}
    </TabLinkMenu>
  );
};

export default Tabs;
